import { Link } from "@tanstack/react-router";
import { cn } from "../lib/utils";
import type { DoubleResume, LivreDetail } from "./contract";
import { compter } from "./format";
import { OngletBrief } from "./histoires-brief";
import {
  BadgeEtatLivre,
  BadgeProvenance,
  ErreurHistoires,
  FilHistoires,
  IntrouvableHistoires,
  LIEN_BOUTON,
  LIEN_BOUTON_PLEIN,
  Signal,
  ZoneIllustration,
} from "./histoires-commun";
import { OngletPersonnages } from "./histoires-personnages";
import { useLivre } from "./histoires-requetes";
import {
  alerteEnClair,
  declarationDuLivre,
  LIBELLE_PROVENANCE,
  type PlanDuLivre,
  pagesCourtes,
  planDuLivre,
  SENS_PROVENANCE,
  titreDouble,
} from "./histoires-textes";
import { ConsoleShell } from "./shell";
import { affichageDeLecture, Chargement, idValide, RelectureEchouee } from "./states";

/**
 * Un livre (`/histoires/livres/$id?onglet=...`), maquettes `HistoiresLivre.dc.html` et
 * `HistoiresBrief.dc.html` : en tete la mesure H2, le nombre de mots et la declaration IA du
 * texte (plan, decision 14) ; puis quatre onglets. Le chemin de fer, par defaut : une carte par
 * double `histoire`, les pages d'avant et d'apres le recit en puces. Le brief et « Coller un
 * manuscrit » (`histoires-brief.tsx`), les personnages (`histoires-personnages.tsx`). La
 * publication arrive au ticket 7 : son onglet est grise.
 *
 * L'onglet passe dans l'adresse : un rechargement, un lien copie ou « Voir le brief » depuis
 * une double y retombent.
 */

export type Onglet = "chemin" | "brief" | "personnages";

export const ONGLETS: readonly { readonly onglet: Onglet; readonly libelle: string }[] = [
  { onglet: "chemin", libelle: "Chemin de fer" },
  { onglet: "brief", libelle: "Brief" },
  { onglet: "personnages", libelle: "Personnages" },
];

export function HistoiresLivrePage({ id, onglet }: { id: string; onglet: Onglet }) {
  const valide = idValide(id);
  const lecture = useLivre(id, valide);
  const affichage = affichageDeLecture(lecture);
  const detail = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilHistoires>{detail?.livre.titre}</FilHistoires>
      {!valide ? <IntrouvableHistoires quoi={`le livre ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="du livre" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurHistoires error={lecture.error} quoi={`le livre ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {detail === undefined ? null : <Livre detail={detail} onglet={onglet} />}
    </ConsoleShell>
  );
}

/** Les mots des doubles `histoire` : ce que la maquette compte en tete du livre. */
export function motsDuRecit(doubles: readonly DoubleResume[]): number {
  return doubles
    .filter((double) => double.type === "histoire")
    .reduce((total, double) => total + double.mots, 0);
}

/** La premiere double `histoire` sans texte : « Écrire la suivante », l'entree du telephone. */
export function suivanteAEcrire(doubles: readonly DoubleResume[]): DoubleResume | undefined {
  return [...doubles]
    .sort((a, b) => a.rang - b.rang)
    .find((double) => double.type === "histoire" && !double.ecrite);
}

function Livre({ detail, onglet }: { detail: LivreDetail; onglet: Onglet }) {
  const { livre, doubles, mesure } = detail;
  const plan = planDuLivre(doubles);
  const part = mesure.doubles === 0 ? 0 : Math.min(100, (mesure.ecrites / mesure.doubles) * 100);
  const sousTitre = [
    livre.cle,
    `${compter(mesure.doubles, "double", "doubles")} sur ${compter(plan.totalPages, "page", "pages")}`,
  ].join(" · ");

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="flex flex-wrap items-center gap-3 text-2xl font-semibold">
            {livre.titre}
            <BadgeEtatLivre etat={livre.etat} />
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
        </div>
        <ul aria-label="Où en est le livre" className="flex flex-wrap gap-2 text-sm">
          <li className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 tabular-nums">
            {compter(mesure.ecrites, "double écrite", "doubles écrites")} sur {mesure.doubles}
            <span
              aria-hidden="true"
              className="block h-1.5 w-14 overflow-hidden rounded-full bg-muted-foreground/25"
            >
              <span className="block h-full bg-foreground" style={{ width: `${part}%` }} />
            </span>
          </li>
          <li className="rounded-full bg-muted px-3 py-1 tabular-nums">
            {compter(motsDuRecit(doubles), "mot", "mots")}
          </li>
          <li className="rounded-full bg-muted px-3 py-1">
            {declarationDuLivre(detail.declaration_texte)}
          </li>
        </ul>
      </header>

      <nav
        aria-label="Onglets du livre"
        className="flex gap-0.5 overflow-x-auto border-b border-border"
      >
        {/* `exact` : sans lui, le routeur tient « Chemin de fer » (sans `onglet`) pour actif sur
            tous les onglets, et lui pose `aria-current`. */}
        {ONGLETS.map((entree) => (
          <Link
            activeOptions={{ exact: true }}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              entree.onglet === onglet
                ? "border-foreground font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            key={entree.onglet}
            params={{ id: String(livre.id) }}
            search={entree.onglet === "chemin" ? {} : { onglet: entree.onglet }}
            to="/histoires/livres/$id"
          >
            {entree.libelle}
          </Link>
        ))}
        <span
          aria-disabled="true"
          className="whitespace-nowrap px-3.5 py-2.5 text-sm text-muted-foreground/60"
          title="arrive avec la publication"
        >
          Publication
        </span>
      </nav>

      {onglet === "chemin" ? <CheminDeFer detail={detail} plan={plan} /> : null}
      {onglet === "brief" ? <OngletBrief detail={detail} /> : null}
      {onglet === "personnages" ? <OngletPersonnages detail={detail} /> : null}
    </>
  );
}

function CheminDeFer({ detail, plan }: { detail: LivreDetail; plan: PlanDuLivre }) {
  const { livre, doubles } = detail;
  const ordre = [...doubles].sort((a, b) => a.rang - b.rang);
  const premiere = ordre.findIndex((double) => double.type === "histoire");
  const derniere = ordre.findLastIndex((double) => double.type === "histoire");
  const avant = premiere === -1 ? ordre : ordre.slice(0, premiere);
  const recit = premiere === -1 ? [] : ordre.slice(premiere, derniere + 1);
  const apres = premiere === -1 ? [] : ordre.slice(derniere + 1);
  const histoires = recit.filter((double) => double.type === "histoire");
  const gabarits = histoires.filter((double) => double.gabarit !== null).length;
  const suivante = suivanteAEcrire(doubles);

  if (ordre.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Ce livre n'a pas encore de double : son plan se pose par{" "}
        <code className="font-mono text-xs">histoire console plan</code>.
      </p>
    );
  }

  return (
    <>
      <section
        aria-label="Pour les images"
        className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-border bg-card px-5 py-3.5 text-card-foreground"
      >
        <p className="text-sm max-md:hidden">
          <strong className="font-semibold">Pour les images</strong>{" "}
          <span className="text-muted-foreground">
            gabarits : {gabarits} sur {histoires.length}
          </span>
        </p>
        <span className="flex flex-wrap gap-2 sm:ml-auto">
          <Link
            className={LIEN_BOUTON}
            params={{ id: String(livre.id) }}
            search={{ onglet: "brief" }}
            to="/histoires/livres/$id"
          >
            Coller un manuscrit
          </Link>
          {suivante === undefined ? null : (
            <Link
              className={LIEN_BOUTON_PLEIN}
              params={{ id: String(suivante.id) }}
              to="/histoires/doubles/$id"
            >
              Écrire la suivante : {titreDouble(suivante, plan.numeros.get(suivante.id))}
            </Link>
          )}
        </span>
      </section>

      <div className="flex flex-col gap-1.5">
        <ul
          aria-label="Provenances du texte"
          className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-xs text-muted-foreground"
        >
          {(["ecrit", "assiste", "genere", null] as const).map((provenance) => (
            <li className="flex items-center gap-1.5" key={provenance ?? "vide"}>
              <BadgeProvenance provenance={provenance} />
              {SENS_PROVENANCE[provenance ?? "vide"]}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground max-md:hidden">
          Dans la zone grise, le numéro de chaque page. Un clic sur une carte ouvre la double ; un
          titre trop long se lit en entier sur la double.
        </p>
      </div>

      {avant.length === 0 ? null : <Autour doubles={avant} plan={plan} titre="Avant le récit" />}

      {recit.length === 0 ? null : (
        <ul
          aria-label="Chemin de fer"
          className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {recit.map((double) => (
            <li className="min-w-0" key={double.id}>
              <CarteDouble double={double} plan={plan} />
            </li>
          ))}
        </ul>
      )}

      {apres.length === 0 ? null : <Autour doubles={apres} plan={plan} titre="Après le récit" />}
    </>
  );
}

/** Le nom d'une carte : son titre, l'etat de son texte, ses alertes. */
export function nomDeCarte(double: DoubleResume, titre: string): string {
  const provenance = double.provenance === null ? null : LIBELLE_PROVENANCE[double.provenance];
  const texte = double.ecrite
    ? `${provenance ?? "vide"}, ${compter(double.mots, "mot", "mots")}`
    : provenance === null
      ? "à écrire"
      : `${provenance}, à écrire`;
  return [titre, texte, ...double.alertes.map(alerteEnClair)].join(", ");
}

/**
 * La carte d'une double : toute la carte est le lien, son nom dit la double et l'etat de son
 * texte ; dedans, la zone grise avec ses numeros de page et la place du texte du gabarit,
 * l'intention, l'extrait ou « à écrire », les alertes, la provenance.
 */
function CarteDouble({ double, plan }: { double: DoubleResume; plan: PlanDuLivre }) {
  const numero = plan.numeros.get(double.id);
  const titre = titreDouble(double, numero);
  const nomCourt = double.type === "histoire" ? double.titre?.trim() || "sans titre" : titre;
  return (
    <Link
      aria-label={nomDeCarte(double, titre)}
      className="flex h-full flex-col gap-2.5 rounded-lg border border-border bg-card p-3 text-card-foreground transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      params={{ id: String(double.id) }}
      to="/histoires/doubles/$id"
    >
      <span className="flex min-w-0 items-center gap-2">
        {numero === undefined ? null : (
          <span className="inline-flex h-[22px] min-w-[22px] flex-none items-center justify-center rounded-md bg-foreground px-1.5 text-xs font-semibold text-background">
            {numero}
          </span>
        )}
        <span className="truncate text-sm font-semibold" title={titre}>
          {nomCourt}
        </span>
      </span>
      {/* Les images restent sur ordinateur (amendement 2) : au telephone, la carte se resserre
          sur le texte. */}
      <span className="block max-md:hidden">
        <ZoneIllustration gabarit={double.gabarit} pages={plan.pages.get(double.id)} />
      </span>
      {double.intention === null || double.intention.trim() === "" ? null : (
        <span className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
          {double.intention}
        </span>
      )}
      {double.extrait === null ? (
        <span className="text-sm text-muted-foreground">à écrire</span>
      ) : (
        <span className="text-sm leading-normal">{double.extrait}</span>
      )}
      {double.alertes.map((alerte) => (
        <Signal key={alerte.type} ton={alerte.type === "mot_interdit" ? "rouge" : "ambre"}>
          {alerteEnClair(alerte)}
        </Signal>
      ))}
      <span className="mt-auto flex items-center gap-2 pt-0.5">
        <BadgeProvenance provenance={double.provenance} />
        {double.ecrite ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {compter(double.mots, "mot", "mots")}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

/** Les pages avant et apres le recit : couverture, titre, liminaire, page pour les parents. */
function Autour({
  titre,
  doubles,
  plan,
}: {
  titre: string;
  doubles: readonly DoubleResume[];
  plan: PlanDuLivre;
}) {
  return (
    <section aria-label={titre} className="flex flex-wrap items-center gap-2.5">
      <span className="w-full text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:w-32">
        {titre}
      </span>
      <ul className="flex flex-wrap gap-2.5">
        {doubles.map((double) => {
          const pages = plan.pages.get(double.id) ?? null;
          const nom = titreDouble(double, plan.numeros.get(double.id));
          return (
            <li key={double.id}>
              <Link
                className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-input bg-card px-3 py-1.5 text-sm hover:border-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                params={{ id: String(double.id) }}
                to="/histoires/doubles/$id"
              >
                {pages === null ? null : (
                  <span className="text-xs text-muted-foreground">{pagesCourtes(pages)}</span>
                )}
                <b className="font-semibold">{nom}</b>
                {double.type === "note" ? <BadgeProvenance provenance={double.provenance} /> : null}
                {double.intention === null || double.intention.trim() === "" ? null : (
                  <span className="text-xs text-muted-foreground">{double.intention}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
